import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { useClientsConfig } from '@/config/clients-config';
import { ContactPersonFormProps } from './types';
import { getContactFieldError, hasContactFieldError } from './validation';

export function ContactPersonForm({
  contacts,
  onChange,
  maxContacts,
  errors = {}
}: ContactPersonFormProps) {
  const config = useClientsConfig();
  const effectiveMaxContacts = maxContacts || config.maxContactPersons;

  const addContact = () => {
    if (contacts.length >= effectiveMaxContacts) return;
    
    onChange([
      ...contacts,
      { name: '', email: '' }
    ]);
  };

  const updateContact = (index: number, field: 'name' | 'email', value: string) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    onChange(newContacts);
  };

  const removeContact = (index: number) => {
    const newContacts = [...contacts];
    newContacts.splice(index, 1);
    onChange(newContacts);
  };

  if (!config.enableContactPersons) {
    return null;
  }

  return (
    <div className="col-span-2 space-y-3">
      <Label className="text-base font-medium">Contact Persons</Label>
      
      {contacts.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No contact persons added yet.
        </p>
      )}
      
      {contacts.map((contact, index) => (
        <div 
          key={index} 
          className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
            <div className="space-y-1">
              <Label 
                htmlFor={`contact-name-${index}`} 
                className="text-xs text-muted-foreground"
              >
                Name {config.requiredFieldMarker}
              </Label>                <Input
                  id={`contact-name-${index}`}
                  value={contact.name}
                  onChange={(e) => updateContact(index, 'name', e.target.value)}
                  placeholder="Contact person name"
                  className={hasContactFieldError(errors, index, 'name') ? 'border-red-500' : ''}
                />
              {hasContactFieldError(errors, index, 'name') && (
                <p className="text-xs text-red-500">
                  {getContactFieldError(errors, index, 'name')}
                </p>
              )}
            </div>
            
            <div className="space-y-1">
              <Label 
                htmlFor={`contact-email-${index}`} 
                className="text-xs text-muted-foreground"
              >
                Email {config.requiredFieldMarker}
              </Label>                <Input
                  id={`contact-email-${index}`}
                  type="email"
                  value={contact.email}
                  onChange={(e) => updateContact(index, 'email', e.target.value)}
                  placeholder="contact@company.com"
                  className={hasContactFieldError(errors, index, 'email') ? 'border-red-500' : ''}
                />
              {hasContactFieldError(errors, index, 'email') && (
                <p className="text-xs text-red-500">
                  {getContactFieldError(errors, index, 'email')}
                </p>
              )}
            </div>
          </div>
          
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="h-9 w-9 mt-6 shrink-0"
            onClick={() => removeContact(index)}
            title="Remove contact person"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      
      {contacts.length < effectiveMaxContacts && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={addContact}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Contact Person
          {effectiveMaxContacts > 1 && (
            <span className="ml-1 text-xs text-muted-foreground">
              ({contacts.length}/{effectiveMaxContacts})
            </span>
          )}
        </Button>
      )}
      
      {contacts.length >= effectiveMaxContacts && (
        <p className="text-xs text-muted-foreground">
          Maximum number of contact persons reached ({effectiveMaxContacts})
        </p>
      )}
    </div>
  );
}
